import { useSearch } from '@tanstack/react-router';
import { useAuthContext } from '@emfood/shared';
import Iconify from '@emfood/shared/src/components/iconify';

import { Stack, Tooltip, IconButton } from '@mui/material';

import AddToCartButton from 'src/components/products/add-to-cart-button';
import UpdateProductButton from 'src/components/products/update-product-button';

import { EnhancedFavouritesItemModel } from './use-favourite-products-list';

export type ActiveOperationType = 'add' | 'remove' | 'update';
// Keyed by row id (favourites item id). Mirrors the product-list model: each row's
// buttons depend only on its own entry — no cross-row disable / flash on sibling rows.
export type ActiveOperations = Map<string, ActiveOperationType>;

interface BasketControlsProps {
  product: EnhancedFavouritesItemModel;
  handleUpdate: (cartItemId: string, quantity: number, productId: string) => void;
  handleAddToCart: (productStockId: string, quantity: number, productId: string) => void;
  handleRemoveFromCart: (cartItemId: string, productId: string) => void;
  activeOperations: ActiveOperations;
}
const BasketControls = ({
  product,
  handleUpdate,
  handleAddToCart,
  handleRemoveFromCart,
  activeOperations,
}: BasketControlsProps) => {
  const thisRowOp = activeOperations.get(product.id);
  const isThisRowMutating = thisRowOp !== undefined;

  if (product.itemInCart) {
    const isSyncedWithCart =
      product.quantity.toString() === product.cartQuantity.toString();
    const isDisabled = isSyncedWithCart || isThisRowMutating;
    return (
      <>
        <UpdateProductButton
          isDisabled={isDisabled}
          isInCart={isSyncedWithCart}
          isLoading={thisRowOp === 'update'}
          onClick={() => handleUpdate(product.cartItemId, product.quantity, product.id)}
          quantity={product.quantity}
          maxQuantity={Number(product.surplusEntry?.reservedQuantity)}
        />
        <Tooltip title="Remove from basket">
          <IconButton
            aria-label="Remove from basket"
            disabled={isThisRowMutating}
            sx={{ ml: 'auto' }}
            onClick={() => handleRemoveFromCart(product.cartItemId, product.id)}
          >
            {thisRowOp === 'remove' ? (
              <Iconify icon="eos-icons:loading" />
            ) : (
              <Iconify icon="solar:trash-bin-trash-bold" />
            )}
          </IconButton>
        </Tooltip>
      </>
    );
  }

  return (
    <AddToCartButton
      isLoading={thisRowOp === 'add'}
      isDisabled={false}
      onClick={() => handleAddToCart(product.productStockId, product.quantity, product.id)}
      quantity={product.quantity}
      maxQuantity={Number(product.surplusEntry?.reservedQuantity)}
    />
  );
};

interface ActionCellProps {
  product: EnhancedFavouritesItemModel;
  handleUpdate: (cartItemId: string, quantity: number, productId: string) => void;
  handleAddToCart: (productStockId: string, quantity: number, productId: string) => void;
  handleRemoveFromCart: (cartItemId: string, productId: string) => void;
  deleteItem: (itemId: string) => Promise<void>;
  activeOperations: ActiveOperations;
}

export const ActionsCell = ({
  product,
  handleAddToCart,
  handleUpdate,
  handleRemoveFromCart,
  deleteItem,
  activeOperations,
}: ActionCellProps) => {
  const { user } = useAuthContext();
  const searchParams = useSearch({ from: '/favourites/' });
  const currentTab = searchParams.tab;

  const isCateringManager =
    user?.roles?.some((role: any) => role.name === 'Catering Manager') || false;
  const canModify = currentTab === 'personal' || isCateringManager;

  return (
    <Stack
      direction="row"
      sx={{ minWidth: '200px', maxWidth: '200px' }}
      alignItems="center"
      justifyContent="start"
      gap={1}
    >
      {canModify && (
        <Tooltip title="Remove from favourites">
          <IconButton onClick={() => deleteItem(product.id)}>
            <Iconify icon="solar:minus-circle-bold" />
          </IconButton>
        </Tooltip>
      )}
      <BasketControls
        product={product}
        handleAddToCart={handleAddToCart}
        handleUpdate={handleUpdate}
        handleRemoveFromCart={handleRemoveFromCart}
        activeOperations={activeOperations}
      />
    </Stack>
  );
};
